import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiDownload, FiUploadCloud, FiFileText, FiAlertTriangle, FiCheckCircle, FiClock, FiChevronDown, FiChevronUp, FiPlay, FiX, FiFile, FiLayers,
} from 'react-icons/fi';
import useEsc from '../../hooks/useEsc';
import GrupoSequencia from './GrupoSequencia';
import css from './ConsultaNfs.module.css';
import {
  listarClientesCaptura, listarPendenciasCaptura, listarNotasCapturadas, listarExecucoesCaptura,
  sincronizarClienteCaptura, executarCapturaTodos, baixarCaptura, urlZip, arquivoCaptura, enviarZipDocs, executarPaulistana, analisarSequencia, justificarSequencia, removerJustificativa,
} from '../../services/nfseCaptura';
import { ehPortal } from '../../utils/portal';

/* ── Consulta NFS ──
   Notas emitidas e recebidas de cada cliente, capturadas automaticamente do
   ADN (Ambiente de Dados Nacional) com o certificado A1 do próprio cliente:
   job diário às 02:00 + "Sincronizar agora". Daqui sai o ZIP pro Domínio. */

const STATUS = {
  nunca: ['Ainda não sincronizado', css.stNeutro], rodando: ['Sincronizando...', css.stRodando], ok: ['Em dia', css.stOk],
  erro: ['Erro na última consulta', css.stErro], limite: ['Limite da Receita: nova tentativa em 1h', css.stAlerta],
  sem_certificado: ['Sem certificado', css.stErro], certificado_vencido: ['Certificado vencido', css.stErro], senha_invalida: ['Senha do certificado inválida', css.stErro],
  sem_autorizacao: ['Prefeitura recusou o certificado do escritório', css.stErro], fora_sp: ['Sem inscrição em São Paulo', css.stNeutro],
};
const moeda = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const doc = (d) => {
  const x = String(d || '').replace(/\D/g, '');
  if (x.length === 14) return x.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (x.length === 11) return x.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return d || '';
};
const RET_TITULO = { ISS: 'ISS retido', IRRF: 'IR retido na fonte', INSS: 'INSS (contribuição previdenciária) retido', PIS: 'PIS retido', COFINS: 'COFINS retido', CSLL: 'CSLL retida' };

/* escolha do conteúdo do ZIP (XML primeiro, que é o que vai pro Domínio) */
function ModalZip({ qtd, onEscolher, onFechar }) {
  useEsc(onFechar);
  /* marcado: além de baixar, o ZIP (sempre XML + PDF) vai pro Docs */
  const [enviarDocs, setEnviarDocs] = useState(false);
  const opcoes = [
    { v: 'xml', icone: <FiFileText size={20} />, titulo: 'Só XML', desc: 'Pastas saidas e tomadas, para o Domínio', destaque: true },
    { v: 'pdf', icone: <FiFile size={20} />, titulo: 'Só PDF', desc: 'DANFSe de cada nota' },
    { v: 'ambos', icone: <FiLayers size={20} />, titulo: 'XML e PDF', desc: 'Em cada pasta, XML e subpasta "pdf"' },
  ];
  return (
    <div className={css.modalFundo}>
      <div className={css.modalZip} role="dialog" aria-modal="true">
        <div className={css.modalTopo}>
          <strong>Baixar ZIP · {qtd} nota(s)</strong>
          <button type="button" className={css.modalX} onClick={onFechar} title="Fechar (Esc)"><FiX size={18} /></button>
        </div>
        <p className={css.modalSub}>O que vai dentro do arquivo?</p>
        <div className={css.zipOpcoes}>
          {opcoes.map(o => (
            <button key={o.v} type="button" className={`${css.zipOpcao} ${o.destaque ? css.zipDestaque : ''}`} onClick={() => onEscolher(o.v, enviarDocs)}>
              {o.icone}<strong>{o.titulo}</strong><small>{o.desc}</small>
            </button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 4px 2px', fontSize: 13, color: '#1e3a5f', cursor: 'pointer' }}>
          <input type="checkbox" checked={enviarDocs} onChange={e => setEnviarDocs(e.target.checked)} />
          <span><strong>Enviar também ao Docs</strong> — o ZIP com XML e PDF do período vai para a pasta do cliente</span>
        </label>
      </div>
    </div>
  );
}

/* PDF aberto dentro do sistema */
function VisualizadorPdf({ pdf, onFechar }) {
  useEsc(onFechar);
  return (
    <div className={css.modalFundo}>
      <div className={css.modalPdf} role="dialog" aria-modal="true">
        <div className={css.modalTopo}>
          <strong>{pdf.titulo}</strong>
          <span className={css.espaco} />
          {pdf.url && !pdf.externo && <a className={css.btnSec} href={pdf.url} download={pdf.nome}><FiDownload size={13} /> Baixar PDF</a>}
          {pdf.externo && <a className={css.btnSec} href={pdf.url} target="_blank" rel="noreferrer">Abrir no site da Prefeitura</a>}
          <button type="button" className={css.modalX} onClick={onFechar} title="Fechar (Esc)"><FiX size={18} /></button>
        </div>
        {pdf.url ? <iframe className={css.pdfFrame} src={pdf.url} title={pdf.titulo} />
          : <div className={css.pdfCarregando}><FiRefreshCw size={22} className={css.girando} /> Montando o PDF...</div>}
      </div>
    </div>
  );
}

const API_SEQ = { justificar: justificarSequencia, remover: removerJustificativa };

function PainelSequencia({ filtroPeriodo, setErro }) {
  const [dados, setDados] = useState(null);
  const chave = JSON.stringify(filtroPeriodo);
  const carregar = useCallback(() => analisarSequencia(filtroPeriodo).then(setDados).catch(e => setErro(e.message)), [chave]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDados(null); carregar(); }, [carregar]);
  if (!dados) return <p className={css.vazio}>Analisando a numeração...</p>;
  const grupos = dados.series;
  return (
    <div className={css.seqWrap}>
      <p className={css.seqAjuda}>
        Notas <strong>emitidas</strong> no período, por série da DPS (numeração do emissor do cliente). Nota cancelada conta como número usado.
        O número pulado na virada do mês também aparece. Se o número foi descartado ou inutilizado de propósito, use <strong>Justificar</strong>.
      </p>
      {!grupos.length && !dados.nfse.quantidade && <p className={css.vazio}>Nenhuma nota emitida nesse período.</p>}
      {grupos.map(g => (
        <GrupoSequencia key={g.serie} g={g} titulo={`Série ${g.serie}`} sub="Numeração da DPS" clienteId={filtroPeriodo.cliente_id} onMudou={carregar} setErro={setErro} api={API_SEQ} />
      ))}
      {dados.nfse.quantidade > 0 && (
        <GrupoSequencia g={dados.nfse} titulo="Número da NFS-e" sub="Gerado pelo sistema nacional; buraco aqui pode ser nota que não chegou na captura"
          clienteId={filtroPeriodo.cliente_id} onMudou={carregar} setErro={setErro} api={API_SEQ} />
      )}
    </div>
  );
}

const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

function ConsultaNfs() {
  const [dados, setDados] = useState({ items: [], ambiente: 2 });
  const [pendencias, setPendencias] = useState([]);
  const [verPendencias, setVerPendencias] = useState(false);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(null);
  const [modoData, setModoData] = useState('competencia');
  const [filtro, setFiltro] = useState({ competencia: mesAtual(), inicio: '', fim: '', tipo: '', situacao: 'ativa', retencao: '' });
  const [notas, setNotas] = useState({ items: [], totais: { quantidade: 0, valor: 0, iss: 0, retencoes: 0, comRetencao: 0 } });
  const [carregandoNotas, setCarregandoNotas] = useState(false);
  const [aba, setAba] = useState('notas');
  const [modalZip, setModalZip] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [execucoes, setExecucoes] = useState([]);
  const [verLog, setVerLog] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const sincronizandoAntes = useRef(false);

  const carregarClientes = useCallback(() => listarClientesCaptura().then(setDados).catch(e => setErro(e.message)), []);
  useEffect(() => { carregarClientes(); listarPendenciasCaptura().then(r => setPendencias(r.items || [])).catch(() => {}); }, [carregarClientes]);

  const cliente = dados.items.find(c => c.id === sel) || null;
  const filtroApi = sel ? {
    cliente_id: sel, tipo: filtro.tipo, situacao: filtro.situacao, retencao: filtro.retencao,
    ...(modoData === 'competencia' ? { competencia: filtro.competencia } : { inicio: filtro.inicio, fim: filtro.fim }),
  } : null;
  const chaveFiltro = JSON.stringify(filtroApi);

  const carregarNotas = useCallback(() => {
    if (!filtroApi) return Promise.resolve();
    setCarregandoNotas(true); setErro('');
    return listarNotasCapturadas(filtroApi).then(setNotas).catch(e => setErro(e.message)).finally(() => setCarregandoNotas(false));
  }, [chaveFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarNotas(); }, [carregarNotas]);
  useEffect(() => { if (sel) listarExecucoesCaptura(sel).then(r => setExecucoes(r.items || [])).catch(() => {}); }, [sel, notas]);

  /* enquanto o cliente sincroniza, acompanha; quando termina, recarrega as notas */
  const sincronizando = !!cliente?.sincronizando || dados.jobRodando;
  useEffect(() => {
    if (!sincronizando) {
      if (sincronizandoAntes.current) { sincronizandoAntes.current = false; carregarNotas(); }
      return undefined;
    }
    sincronizandoAntes.current = true;
    const t = setInterval(carregarClientes, 4000);
    return () => clearInterval(t);
  }, [sincronizando, carregarClientes, carregarNotas]);

  async function sincronizar() {
    setErro(''); setMsg('');
    try { await sincronizarClienteCaptura(sel); setMsg('Sincronização iniciada. A primeira carga pode levar alguns minutos.'); await carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function capturarTodos() {
    if (!window.confirm('Capturar agora as notas de todos os clientes com certificado? Roda em segundo plano, um cliente por vez.')) return;
    setErro(''); setMsg('');
    try { await executarCapturaTodos(); setMsg('Captura de todos iniciada em segundo plano.'); carregarClientes(); } catch (e) { setErro(e.message); }
  }
  async function buscarPaulistana() {
    if (!window.confirm('Buscar na Nota Fiscal Paulistana, com o certificado do escritório, as notas dos clientes SEM certificado A1?\n\nSó entram os nossos clientes que têm inscrição em São Paulo e cadastraram o escritório como contador na Prefeitura.')) return;
    setErro(''); setMsg('');
    try { await executarPaulistana(); setMsg('Busca na Nota Paulistana iniciada em segundo plano. Os clientes encontrados aparecem na lista com a etiqueta NFPAULISTANA.'); carregarClientes(); }
    catch (e) { setErro(e.message); }
  }
  async function baixar(caminho, nome) {
    setErro('');
    try { await baixarCaptura(caminho, nome); } catch (e) { setErro(e.message); }
  }
  async function verPdf(n) {
    setErro('');
    const titulo = `NFS-e nº ${n.numero || '—'} · ${n.tipo === 'emitida' ? n.tomador_nome || '' : n.prestador_nome || ''}`;
    /* nota da Paulistana: o PDF é o oficial da Prefeitura de SP */
    if (n.origem === 'nfpaulistana') {
      if (n.link_oficial) setPdf({ titulo, url: n.link_oficial, externo: true });
      else setErro('Nota da Prefeitura de SP sem link do PDF oficial.');
      return;
    }
    setPdf({ titulo });
    try {
      const a = await arquivoCaptura(`/notas/${n.id}/pdf`);
      setPdf(atual => (atual && atual.titulo === titulo ? { titulo, url: a.url, nome: `${n.chave_acesso}.pdf` } : atual));
    } catch (e) { setPdf(null); setErro(e.message); }
  }
  function fecharPdf() {
    setPdf(atual => { if (atual?.url && !atual.externo) URL.revokeObjectURL(atual.url); return null; });
  }
  function baixarZip(conteudo, tambemDocs) {
    setModalZip(false);
    baixar(urlZip({ ...filtroApi, conteudo }), 'notas.zip');
    if (tambemDocs) enviarDocs();
  }
  /* o período e os filtros da tela viram um ZIP (XML + PDF) na pasta do
     cliente no Docs; o resultado aparece aqui mesmo, perto do botão */
  const [enviandoDocs, setEnviandoDocs] = useState(false);
  async function enviarDocs() {
    setErro(''); setMsg(''); setEnviandoDocs(true);
    try {
      const r = await enviarZipDocs(filtroApi);
      setMsg(`Enviado ao Docs: ${r.arquivo} (${r.quantidade} nota(s), XML e PDF).`);
    } catch (e) { setErro(e.message); }
    finally { setEnviandoDocs(false); }
  }

  /* usuário de cliente: a lista é só ele -- já entra selecionado e sem a
     barra lateral de clientes, com a tela inteira pras notas */
  const portal = ehPortal();
  useEffect(() => {
    if (portal && !sel && dados.items[0]) setSel(dados.items[0].id);
  }, [portal, sel, dados.items]);

  const q = busca.trim().toLowerCase();
  const lista = dados.items.filter(c => !q || `${c.codigo} ${c.nome}`.toLowerCase().includes(q));
  const [stTexto, stCls] = STATUS[cliente?.sincronizando ? 'rodando' : (cliente?.status || 'nunca')] || STATUS.nunca;

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiFileText size={20} /></div>
        <div>
          <h1 className={css.titulo}>Consulta NFS</h1>
          <p className={css.sub}>NFS-e emitidas e recebidas, capturadas do Ambiente de Dados Nacional com o certificado A1 de cada cliente</p>
        </div>
        <span className={dados.ambiente === 1 ? css.ambProd : css.ambHom}>{dados.ambiente === 1 ? 'Produção' : 'Homologação'}</span>
        {!ehPortal() && (
        <button type="button" className={css.btnSec} onClick={buscarPaulistana} disabled={dados.jobRodando} title="Clientes sem A1, pelo certificado do escritório">
          <span className={css.tagPaulistana}>NFPAULISTANA</span> Buscar
        </button>
        )}
        {!ehPortal() && (
        <button type="button" className={css.btnSec} onClick={capturarTodos} disabled={dados.jobRodando}>
          <FiPlay size={13} /> {dados.jobRodando ? 'Capturando todos...' : 'Capturar todos agora'}
        </button>
        )}
      </div>

      {pendencias.length > 0 && (
        <div className={css.pendencias}>
          <button type="button" onClick={() => setVerPendencias(v => !v)}>
            <FiAlertTriangle size={14} /> {pendencias.length} cliente(s) com pendência na captura (certificado vencido, senha inválida ou erro)
            {verPendencias ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </button>
          {verPendencias && (
            <ul>
              {pendencias.map(p => (
                <li key={p.id}><strong>{p.codigo} - {p.nome}</strong>: {(STATUS[p.status] || [p.status])[0]}{p.ultimo_erro ? ` — ${p.ultimo_erro}` : ''}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {erro && <p className={css.erro}>{erro}</p>}
      {msg && <p className={css.ok}>{msg}</p>}

      <div className={css.corpo} style={portal ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}>
        {!portal && (
        <aside className={css.lista}>
          <div className={css.busca}><FiSearch size={13} /><input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
          <p className={css.listaInfo}>{dados.items.length} cliente(s) com certificado</p>
          {lista.map(c => {
            const [, cls] = STATUS[c.sincronizando ? 'rodando' : (c.status || 'nunca')] || STATUS.nunca;
            return (
              <button key={c.id} type="button" className={`${css.itemCliente} ${sel === c.id ? css.itemAtivo : ''}`} onClick={() => setSel(c.id)}>
                <span className={`${css.ponto} ${cls}`} />
                <span className={css.itemTexto}><strong>{c.codigo} - {c.nome}</strong><small>{c.paulistana && <span className={css.tagPaulistana}>NFPAULISTANA</span>}{c.notas} nota(s) · {c.ultima_sincronizacao ? quando(c.ultima_sincronizacao) : 'nunca sincronizado'}</small></span>
              </button>
            );
          })}
          {!lista.length && <p className={css.vazio}>Nenhum cliente com certificado A1 cadastrado no menu Certificados.</p>}
        </aside>
        )}

        <section className={css.detalhe}>
          {!cliente ? (
            <div className={css.placeholder}><FiFileText size={34} /><p>Escolha um cliente para ver as notas.</p></div>
          ) : (
            <>
              <div className={css.cliTopo}>
                <div>
                  <h2>{cliente.codigo} - {cliente.nome}</h2>
                  {cliente.paulistana
                    ? <p>{doc(cliente.cnpj)} · CCM {cliente.ccm} · <span className={css.tagPaulistana}>NFPAULISTANA</span> notas da Prefeitura de SP pelo certificado do escritório</p>
                    : <p>{doc(cliente.cnpj)} · certificado vence {dataBr(cliente.certificado_vence)} · último NSU {cliente.ultimo_nsu ?? 0}</p>}
                  <span className={`${css.status} ${stCls}`}>{stTexto}{cliente.status !== 'ok' && cliente.ultimo_erro ? `: ${cliente.ultimo_erro}` : ''}</span>
                </div>
                {!ehPortal() && (
                <button type="button" className={css.btnPri} onClick={sincronizar} disabled={sincronizando}>
                  <FiRefreshCw size={13} className={sincronizando ? css.girando : ''} /> {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
                </button>
                )}
              </div>

              <div className={css.abas}>
                <button type="button" className={aba === 'notas' ? css.abaAtiva : ''} onClick={() => setAba('notas')}>Notas</button>
                <button type="button" className={aba === 'sequencia' ? css.abaAtiva : ''} onClick={() => setAba('sequencia')}>Sequência das emitidas</button>
              </div>

              <div className={css.filtros}>
                <div className={css.segmento}>
                  <button type="button" className={modoData === 'competencia' ? css.segAtivo : ''} onClick={() => setModoData('competencia')}>Competência</button>
                  <button type="button" className={modoData === 'periodo' ? css.segAtivo : ''} onClick={() => setModoData('periodo')}>Período</button>
                </div>
                {modoData === 'competencia' ? (
                  <input type="month" className={css.input} value={filtro.competencia} onChange={e => setFiltro(f => ({ ...f, competencia: e.target.value }))} />
                ) : (
                  <>
                    <input type="date" className={css.input} value={filtro.inicio} onChange={e => setFiltro(f => ({ ...f, inicio: e.target.value }))} title="Data inicial (emissão)" />
                    <span className={css.ate}>até</span>
                    <input type="date" className={css.input} value={filtro.fim} onChange={e => setFiltro(f => ({ ...f, fim: e.target.value }))} title="Data final (emissão)" />
                  </>
                )}
                {aba === 'notas' && (<>
                <select className={css.input} value={filtro.tipo} onChange={e => setFiltro(f => ({ ...f, tipo: e.target.value }))}>
                  <option value="">Emitidas e recebidas</option>
                  <option value="emitida">Emitidas</option>
                  <option value="recebida">Recebidas</option>
                </select>
                <select className={css.input} value={filtro.situacao} onChange={e => setFiltro(f => ({ ...f, situacao: e.target.value }))}>
                  <option value="ativa">Ativas</option>
                  <option value="cancelada">Canceladas/substituídas</option>
                  <option value="todas">Todas</option>
                </select>
                <select className={css.input} value={filtro.retencao} onChange={e => setFiltro(f => ({ ...f, retencao: e.target.value }))}>
                  <option value="">Com e sem retenção</option>
                  <option value="com">Com retenção</option>
                  <option value="sem">Sem retenção</option>
                </select>
                <span className={css.espaco} />
                {!ehPortal() && (
                <button type="button" className={css.btnSec} disabled={!notas.items.length || enviandoDocs} onClick={enviarDocs}
                  title="Envia ao Docs um ZIP com XML e PDF das notas deste filtro">
                  <FiUploadCloud size={13} /> {enviandoDocs ? 'Enviando ao Docs...' : 'Enviar ao Docs'}
                </button>
                )}
                <button type="button" className={css.btnPri} disabled={!notas.items.length} onClick={() => setModalZip(true)}>
                  <FiDownload size={13} /> Baixar ZIP
                </button>
                </>)}
              </div>

              {aba === 'sequencia' ? (
                <PainelSequencia setErro={setErro}
                  filtroPeriodo={{ cliente_id: sel, ...(modoData === 'competencia' ? { competencia: filtro.competencia } : { inicio: filtro.inicio, fim: filtro.fim }) }} />
              ) : (<>

              <div className={`${css.totais} ${css.totais4}`}>
                <div><span>Notas</span><strong>{notas.totais.quantidade}</strong></div>
                <div><span>Valor dos serviços</span><strong>{moeda(notas.totais.valor)}</strong></div>
                <div><span>ISS</span><strong>{moeda(notas.totais.iss)}</strong></div>
                <div><span>Retenções{notas.totais.comRetencao ? ` · ${notas.totais.comRetencao} nota(s)` : ''}</span><strong>{moeda(notas.totais.retencoes)}</strong></div>
              </div>

              <div className={css.tabelaWrap}>
                <table className={css.tabela}>
                  <thead>
                    <tr><th>Emissão</th><th>Nº</th><th>Competência</th><th>Tipo</th><th>Prestador / Tomador</th><th className={css.num}>Valor</th><th className={css.num}>ISS</th><th>Retenções</th><th>Situação</th><th /></tr>
                  </thead>
                  <tbody>
                    {carregandoNotas ? <tr><td colSpan={10} className={css.vazio}>Carregando...</td></tr>
                      : !notas.items.length ? <tr><td colSpan={10} className={css.vazio}>Nenhuma nota nesse filtro.</td></tr>
                        : notas.items.map(n => {
                          const outro = n.tipo === 'emitida' ? { rot: 'Tomador', nome: n.tomador_nome, doc: n.tomador_doc } : { rot: 'Prestador', nome: n.prestador_nome, doc: n.prestador_doc };
                          return (
                            <tr key={n.id} className={n.situacao !== 'ativa' ? css.linhaCancelada : ''}>
                              <td>{dataBr(n.data_emissao)}</td>
                              <td>{n.numero || '—'}</td>
                              <td>{n.competencia ? n.competencia.slice(0, 7).split('-').reverse().join('/') : '—'}</td>
                              <td><span className={n.tipo === 'emitida' ? css.tEmitida : css.tRecebida}>{n.tipo === 'emitida' ? 'Emitida' : 'Recebida'}</span>
                                {n.origem === 'nfpaulistana' && <span className={css.tagPaulistana} title="Nota buscada na Nota Fiscal Paulistana (Prefeitura de SP)">NFPAULISTANA</span>}</td>
                              <td className={css.contra}><small>{outro.rot}</small>{outro.nome || '—'}<em>{doc(outro.doc)}</em></td>
                              <td className={css.num}>{moeda(n.valor_servico)}</td>
                              <td className={css.num}>{moeda(n.valor_iss)}{n.iss_retido ? <em className={css.retido}>retido</em> : null}</td>
                              <td className={css.retCel}>
                                {n.retencoes?.length ? n.retencoes.map(t => <span key={t} className={css.retTag} title={RET_TITULO[t] || t}>{t}</span>)
                                  : <span className={css.retNao}>—</span>}
                              </td>
                              <td>{n.situacao === 'ativa' ? <span className={css.sAtiva}><FiCheckCircle size={11} /> Ativa</span>
                                : <span className={css.sCancelada}>{n.situacao === 'substituida' ? 'Substituída' : 'Cancelada'}</span>}</td>
                              <td className={css.acoes}>
                                <button type="button" title="Baixar XML" onClick={() => baixar(`/notas/${n.id}/xml`, `${n.chave_acesso}.xml`)}>XML</button>
                                <button type="button" title="Ver PDF (DANFSe)" onClick={() => verPdf(n)}>PDF</button>
                              </td>
                            </tr>
                          );
                        })}
                  </tbody>
                </table>
              </div>

              </>)}

              <div className={css.log}>
                <button type="button" onClick={() => setVerLog(v => !v)}><FiClock size={13} /> Execuções deste cliente {verLog ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />}</button>
                {verLog && (
                  <table className={css.tabela}>
                    <thead><tr><th>Início</th><th>Origem</th><th>Notas novas</th><th>Eventos</th><th>Erros</th><th>Duração</th><th>Mensagem</th></tr></thead>
                    <tbody>
                      {execucoes.map(e => (
                        <tr key={e.id}>
                          <td>{quando(e.inicio)}</td><td>{e.origem}</td><td>{e.notas_novas}</td><td>{e.eventos}</td><td>{e.erros}</td>
                          <td>{e.duracao_ms != null ? `${Math.round(e.duracao_ms / 1000)}s` : '…'}</td><td className={css.msgLog}>{e.mensagem || '—'}</td>
                        </tr>
                      ))}
                      {!execucoes.length && <tr><td colSpan={7} className={css.vazio}>Nenhuma execução ainda.</td></tr>}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      {modalZip && <ModalZip qtd={notas.totais.quantidade} onEscolher={baixarZip} onFechar={() => setModalZip(false)} />}
      {pdf && <VisualizadorPdf pdf={pdf} onFechar={fecharPdf} />}
    </div>
  );
}

export default ConsultaNfs;
